import type { FC } from "react";
import {
  CheckIcon,
  ComputerIcon,
  MoonIcon,
  SunIcon,
} from "@/components/ui/icons/ThemeIcons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/primitives/Dropdown";
import { ColorScheme, useTheme } from "@/context/ThemeContext";
import { useThemePreview } from "@/hooks/useThemePreview";
import { cn } from "@/utils/cn";
import { themeComponentStyles } from "@/utils/themeUtils";

export interface SchemeSelectProps {
  /** Additional CSS classes */
  className?: string;
  /** Delay in ms before showing color scheme preview when hovering (default: 450ms) */
  previewDelay?: number;
  /** Whether to disable the preview functionality (default: false) */
  disablePreview?: boolean;
}

/**
 * ColorSchemeDropdown
 *
 * A dropdown using Radix UI DropdownMenu for selecting between light, dark, and system color schemes.
 * This component controls the color scheme (light/dark), NOT the theme colors/palette.
 * Uses Radix UI for proper positioning and viewport awareness on mobile devices.
 */
export const SchemeSelect: FC<SchemeSelectProps> = ({
  className,
  previewDelay = 450,
  disablePreview = false,
}) => {
  const { colorScheme, setColorScheme, previewColorScheme } = useTheme();

  // Color scheme options
  const schemes = [
    { icon: SunIcon, id: ColorScheme.LIGHT, label: "Light" },
    { icon: ComputerIcon, id: ColorScheme.SYSTEM, label: "System" },
    { icon: MoonIcon, id: ColorScheme.DARK, label: "Dark" },
  ];

  // Use theme preview hook to manage preview behavior
  const { handleMouseEnter, handleMouseLeave, handleClose } = useThemePreview(
    previewColorScheme,
    { delay: previewDelay, disabled: disablePreview },
  );

  // Get current scheme icon
  const CurrentSchemeIcon =
    schemes.find((s) => s.id === colorScheme)?.icon || schemes[0].icon;

  const content = (
    <DropdownMenu onOpenChange={(open) => !open && handleClose()}>
      <DropdownMenuTrigger
        aria-label="Select color scheme"
        className={cn(
          "flex items-center gap-2 text-sm px-2.5 cursor-pointer flex-shrink-0",
          themeComponentStyles.getContainerClasses(),
          "hover:bg-gray-100 dark:hover:bg-gray-700/50",
          themeComponentStyles.getControlClasses(),
        )}
      >
        {/* Current scheme icon */}
        <CurrentSchemeIcon className="h-4 w-4" />
        <svg
          aria-hidden="true"
          className="h-4 w-4 hidden sm:block"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <title>Toggle dropdown</title>
          <path
            d="M19 9l-7 7-7-7"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[12rem] p-1"
        sideOffset={4}
      >
        <DropdownMenuLabel>Color Scheme</DropdownMenuLabel>

        {schemes.map((schemeOption) => {
          const isActive = colorScheme === schemeOption.id;
          const Icon = schemeOption.icon;

          return (
            <DropdownMenuItem
              className={cn(
                "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
                isActive
                  ? "bg-gray-100 dark:bg-gray-700/50 text-theme-primary dark:text-theme-primary"
                  : "hover:bg-gray-100/70 dark:hover:bg-gray-700/40 text-gray-700 dark:text-gray-200",
              )}
              key={schemeOption.id}
              onMouseEnter={() => handleMouseEnter(schemeOption.id)}
              onMouseLeave={handleMouseLeave}
              onSelect={() => setColorScheme(schemeOption.id)}
            >
              <Icon className="h-4 w-4" />
              <span className="font-medium">{schemeOption.label}</span>
              {isActive && (
                <CheckIcon className="h-4 w-4 ml-auto text-theme-primary" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return className ? <div className={className}>{content}</div> : content;
};
